/**
 * The admin shell - spec 12's own deliverable: sidebar, top bar, and the chrome
 * every section renders inside.
 *
 * Two decisions worth naming:
 *
 * 1. **The nav badges FR-8.2's two pending counts.** They are fetched from
 *    `/admin/dashboard` - the same call the Dashboard screen makes, so the badge
 *    and the tile cannot disagree. A failure is swallowed: the counts are
 *    decoration on a nav item, and an unreadable queue count must not stop an
 *    admin from opening the queue itself. The refresh is a one-minute poll rather
 *    than anything real-time, because nothing in this app is real-time and a
 *    stale-by-a-minute count is honest about it.
 * 2. **Sign out is a normal button, not a menu.** There is one account per
 *    session, no switching, and no settings behind it.
 * 3. **Below 1024px the sidebar is an off-canvas drawer.** A hamburger in the top
 *    bar opens it; a nav tap, the backdrop or Escape closes it. The identity
 *    and Sign out move into the drawer there, because the top bar has no room.
 *    While it is open the page behind is inert and unscrollable, focus moves into
 *    the drawer, and closing it hands focus back to the hamburger.
 */

import { useEffect, useRef, useState, type RefObject } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";

import { fetchDashboard } from "../lib/api/dashboard";
import { useAuth } from "../lib/auth";
import { NAV, NAV_SECTIONS, activeNavItem } from "../nav";
import { Button } from "./primitives";

/** One minute: fast enough that a queue an admin just cleared looks cleared. */
const COUNT_POLL_MS = 60_000;

function useNavCounts(): Record<string, number> {
  const [counts, setCounts] = useState<Record<string, number>>({});

  useEffect(() => {
    let active = true;

    async function load() {
      try {
        const dashboard = await fetchDashboard();
        if (!active) return;
        setCounts({
          pending_users: dashboard.pending_users,
          pending_family_biz: dashboard.pending_family_biz,
        });
      } catch {
        // See the module docstring: a badge is not worth an error state.
      }
    }

    void load();
    const timer = window.setInterval(load, COUNT_POLL_MS);
    return () => {
      active = false;
      window.clearInterval(timer);
    };
  }, []);

  return counts;
}

export function AppShell() {
  const { admin, signOut } = useAuth();
  const location = useLocation();
  const counts = useNavCounts();
  const active = activeNavItem(location.pathname);
  const [navOpen, setNavOpen] = useState(false);
  // No `finally` resetting this: signing out unmounts the shell and navigates to
  // the login screen, so the only thing a reset could do is set state on a
  // component that is already gone.
  const [signingOut, setSigningOut] = useState(false);

  function handleSignOut() {
    setSigningOut(true);
    void signOut();
  }

  const sidebarRef = useRef<HTMLElement>(null);
  const menuRef = useRef<HTMLButtonElement>(null);
  const wasOpen = useRef(false);

  // Navigating is the drawer's natural "done", so a route change closes it. A tap
  // on the current page's item changes no route, hence the NavLink onClick too.
  useEffect(() => {
    setNavOpen(false);
  }, [location.key]);

  // Growing past the drawer breakpoint (rotating a tablet) must not leave the
  // page inert behind a drawer that is no longer a drawer.
  useEffect(() => {
    const query = window.matchMedia("(min-width: 1025px)");
    function onChange(event: MediaQueryListEvent) {
      if (event.matches) setNavOpen(false);
    }
    query.addEventListener("change", onChange);
    return () => query.removeEventListener("change", onChange);
  }, []);

  // Modal behaviour: lock page scroll while open; move focus in on open and back
  // to the hamburger on close (only if focus was inside the drawer, so a close
  // by tapping the backdrop or the hamburger itself does not steal it).
  useEffect(() => {
    if (navOpen) {
      wasOpen.current = true;
      sidebarRef.current?.querySelector<HTMLElement>("a")?.focus();
      const previous = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = previous;
      };
    }
    if (wasOpen.current) {
      wasOpen.current = false;
      const focused = document.activeElement;
      if (!focused || focused === document.body || sidebarRef.current?.contains(focused)) {
        menuRef.current?.focus();
      }
    }
  }, [navOpen]);

  useEffect(() => {
    if (!navOpen) return;
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") setNavOpen(false);
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [navOpen]);

  // The page title, updated on navigation so a browser tab and a screen reader
  // both say which section is open.
  useEffect(() => {
    document.title = active ? `${active.label} - Fitzone Care Admin` : "Fitzone Care - Admin";
  }, [active]);

  return (
    <div className={`shell ${navOpen ? "shell--nav-open" : ""}`}>
      <aside className="sidebar" id="admin-sidebar" ref={sidebarRef}>
        <div className="wordmark">
          <span>fitzone</span>
          <span className="wordmark__care">care</span>
          <span className="label" style={{ color: "rgb(255 255 255 / 45%)", marginLeft: 2 }}>
            admin
          </span>
        </div>

        <nav aria-label="Sections">
          {NAV_SECTIONS.map((section) => (
            <div key={section} style={{ marginBottom: 18 }}>
              <p className="sidebar__section">{section}</p>
              <div className="nav">
                {NAV.filter((item) => item.section === section).map((item) => (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    onClick={() => setNavOpen(false)}
                    // Exact match when another nav item lives under this path
                    // (`/settings` vs `/settings/email`), so only one is lit.
                    end={item.path === "/" || NAV.some((other) => other.path.startsWith(`${item.path}/`))}
                    className={({ isActive }) => `nav__item ${isActive ? "nav__item--active" : ""}`}
                  >
                    <span>{item.label}</span>
                    {item.counter && (counts[item.counter] ?? 0) > 0 ? (
                      <span className="nav__count">{counts[item.counter]}</span>
                    ) : null}
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>

        <div className="sidebar__account">
          <div className="identity">
            <div className="identity__name">{admin?.full_name ?? "Admin"}</div>
            <div className="identity__role">{admin?.email ?? "Administrator"}</div>
          </div>
          <Button size="sm" disabled={signingOut} onClick={handleSignOut}>
            {signingOut ? "Signing out…" : "Sign out"}
          </Button>
        </div>
      </aside>

      <div className="shell__backdrop" onClick={() => setNavOpen(false)} aria-hidden="true" />

      <div className="main">
        <TopBar
          title={active?.label ?? "Admin"}
          subtitle={active?.hint}
          name={admin?.full_name ?? "Admin"}
          email={admin?.email ?? null}
          signingOut={signingOut}
          onSignOut={handleSignOut}
          navOpen={navOpen}
          menuRef={menuRef}
          onToggleNav={() => setNavOpen((open) => !open)}
        />
        {/* Inert while the drawer is open: no Tab or tap reaches the page behind it.
            The top bar stays live so the hamburger can close the drawer. */}
        <main className="content" inert={navOpen}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}

function TopBar({
  title,
  subtitle,
  name,
  email,
  signingOut,
  onSignOut,
  navOpen,
  menuRef,
  onToggleNav,
}: {
  title: string;
  subtitle?: string;
  name: string;
  email: string | null;
  signingOut: boolean;
  onSignOut: () => void;
  navOpen: boolean;
  menuRef: RefObject<HTMLButtonElement | null>;
  onToggleNav: () => void;
}) {
  return (
    <header className="topbar">
      <button
        type="button"
        ref={menuRef}
        className="topbar__menu"
        aria-label={navOpen ? "Close menu" : "Open menu"}
        aria-expanded={navOpen}
        aria-controls="admin-sidebar"
        onClick={onToggleNav}
      >
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
          <path d="M3 5h14M3 10h14M3 15h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
      </button>
      <div className="topbar__titles">
        <h1 className="topbar__title">{title}</h1>
        {subtitle ? <p className="topbar__subtitle">{subtitle}</p> : null}
      </div>
      <div className="topbar__right">
        <div className="identity">
          <div className="identity__name">{name}</div>
          <div className="identity__role">{email ?? "Administrator"}</div>
        </div>
        <Button size="sm" disabled={signingOut} onClick={onSignOut}>
          {signingOut ? "Signing out…" : "Sign out"}
        </Button>
      </div>
    </header>
  );
}

/** A screen's outer wrapper. Re-exported from `primitives` so a page imports its
 *  frame and its controls from one module. */
export { Page } from "./primitives";
